import React, {useEffect, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import store from '../../store';
import ExclamationMarkIcon from '../../bvcomponents/ui/icons/exclamationMarkIcon';
import WithOverlay from '../../bvcomponents/popups/withOverlayHoc';
import {COLORS} from '../../colorThemes';
import workspace from '../../bvcomponents/sqft/components/canvas/core/workspace';
import {prepareSQFTDataCore} from '../../bvcomponents/sqft/components/canvas/core/workspace/workspaceDataMapper';
import {getActiveSQFTQuoteIndexFromState} from '../../bvcomponents/sqft/helpers/sqftQuoteIndexHelper';
import {
  closeMessageBox,
  fatalErrorRecoveryRestoreEvent,
  openSaveAsNewQuotePopup,
  saveCurrentQuote,
} from './errorRecoveryPopup';
import {buildFatalErrorRecoverySummary} from './fatalErrorRecoverySummary';

const PROMPT_STAGE = 'prompt';
const REVIEW_STAGE = 'review';

function hasSavedQuote() {
  const state = store.getState();

  return Boolean(
    document.kvConfig && document.kvConfig.jobId
    || state.serverState && state.serverState.jobId
  );
}

function shouldUseCurrentCalculatorData(state, quote) {
  if (!state.sqftState) {
    return false;
  }

  if (state.sqftState.isSquareFootageEnabled || state.sqftState.isSQFTInVisualizer) {
    return true;
  }

  return Boolean(quote && quote.sqFtCalcData && workspace.isInitialized && !workspace.isEmpty());
}

// The review uses the same active SQFT workspace that quote saving will snapshot.
function getCalculatorDataOverrides(state) {
  const quoteIndex = getActiveSQFTQuoteIndexFromState(state);
  const quotes = state.quotesState && Array.isArray(state.quotesState.quotes)
    ? state.quotesState.quotes
    : [];
  const quote = quotes[quoteIndex];

  if (!Number.isInteger(quoteIndex) || !shouldUseCurrentCalculatorData(state, quote)) {
    return {};
  }

  try {
    return {
      [quoteIndex]: prepareSQFTDataCore(),
    };
  } catch (error) {
    // Quote saving also falls back to the last quote snapshot if SQFT export fails.
    return {};
  }
}

function getRecoverySummary() {
  const state = store.getState();

  return buildFatalErrorRecoverySummary(state, getCalculatorDataOverrides(state));
}

function getCalculatorEntryLabel(entry) {
  if (entry.labelId) {
    return <FormattedMessage id={entry.labelId}/>;
  }

  return entry.fallbackLabel;
}

const RecoveryCartItems = ({cartItems}) => (
  <div className="fatal-recovery-room__section">
    <div className="fatal-recovery-room__section-title">
      <FormattedMessage
        id="messageBox.fatalErrorReviewCartItems"
        values={{count: cartItems.length}}
      />
    </div>
    {cartItems.length ? (
      <ul className="fatal-recovery-list">
        {cartItems.map((cartItem, itemIndex) => (
          <li className="fatal-recovery-list__item" key={cartItem.id || itemIndex}>
            <span className="fatal-recovery-list__name">{cartItem.name}</span>
            {cartItem.quantity !== null && cartItem.quantity !== undefined ? (
              <span className="fatal-recovery-list__quantity">
                {cartItem.quantity}{cartItem.unit ? ' ' + cartItem.unit : ''}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    ) : (
      <div className="fatal-recovery-room__empty">
        <FormattedMessage id="messageBox.fatalErrorReviewNoCartItems"/>
      </div>
    )}
  </div>
);

RecoveryCartItems.propTypes = {
  cartItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    unit: PropTypes.string,
  })).isRequired,
};

const RecoveryCalculator = ({calculator}) => (
  <div className="fatal-recovery-room__section">
    <div className="fatal-recovery-room__section-title">
      <FormattedMessage id="messageBox.fatalErrorReviewCalculator"/>
    </div>
    {calculator ? (
      <React.Fragment>
        <div className="fatal-recovery-calculator__area">
          <FormattedMessage id="messageBox.fatalErrorReviewArea"/>
          <strong>{calculator.area} {calculator.areaUnit}</strong>
        </div>
        {calculator.entries.length ? (
          <ul className="fatal-recovery-list fatal-recovery-list--calculator">
            {calculator.entries.map((entry, entryIndex) => (
              <li
                className="fatal-recovery-list__item"
                key={(entry.labelId || entry.fallbackLabel) + '-' + entry.featureName + '-' + entryIndex}
              >
                <span className="fatal-recovery-list__name">
                  {getCalculatorEntryLabel(entry)}
                  {entry.featureName ? ' — ' + entry.featureName : ''}
                </span>
                <span className="fatal-recovery-list__quantity">{entry.count}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="fatal-recovery-room__empty">
            <FormattedMessage id="messageBox.fatalErrorReviewNoCalculatorItems"/>
          </div>
        )}
      </React.Fragment>
    ) : (
      <div className="fatal-recovery-room__empty">
        <FormattedMessage id="messageBox.fatalErrorReviewNoCalculator"/>
      </div>
    )}
  </div>
);

RecoveryCalculator.propTypes = {
  calculator: PropTypes.shape({
    area: PropTypes.number,
    areaUnit: PropTypes.string,
    entries: PropTypes.arrayOf(PropTypes.shape({
      count: PropTypes.number,
      fallbackLabel: PropTypes.string,
      featureName: PropTypes.string,
      labelId: PropTypes.string,
    })),
  }),
};

const RecoveryReview = ({summary}) => (
  <React.Fragment>
    <div className="fatal-recovery-review__intro">
      <FormattedMessage id="messageBox.fatalErrorReviewIntro"/>
    </div>
    <div className="fatal-recovery-review__totals">
      <FormattedMessage
        id="messageBox.fatalErrorReviewTotals"
        values={{
          cartItems: summary.cartItemCount,
          rooms: summary.rooms.length,
        }}
      />
    </div>
    <div className="fatal-recovery-review__rooms">
      {summary.rooms.map(room => (
        <section className="fatal-recovery-room" key={room.index}>
          <h3 className="fatal-recovery-room__title">{room.name}</h3>
          <div className="fatal-recovery-room__content">
            <RecoveryCartItems cartItems={room.cartItems}/>
            <RecoveryCalculator calculator={room.calculator}/>
          </div>
        </section>
      ))}
    </div>
  </React.Fragment>
);

RecoveryReview.propTypes = {
  summary: PropTypes.shape({
    cartItemCount: PropTypes.number,
    rooms: PropTypes.arrayOf(PropTypes.shape({
      calculator: RecoveryCalculator.propTypes.calculator,
      cartItems: RecoveryCartItems.propTypes.cartItems,
      index: PropTypes.number,
      name: PropTypes.string,
    })),
  }).isRequired,
};

const FatalErrorRecoveryPopup = ({initialStage = PROMPT_STAGE}) => {
  const [stage, setStage] = useState(initialStage);
  const [summary, setSummary] = useState(() => (
    initialStage === REVIEW_STAGE ? getRecoverySummary() : null
  ));
  const [visible, setVisible] = useState(true);
  const quoteCanBeUpdated = hasSavedQuote();

  useEffect(() => {
    const restoreReview = () => {
      setSummary(getRecoverySummary());
      setStage(REVIEW_STAGE);
      setVisible(true);
    };

    window.addEventListener(fatalErrorRecoveryRestoreEvent, restoreReview);

    return () => window.removeEventListener(fatalErrorRecoveryRestoreEvent, restoreReview);
  }, []);

  if (!visible) {
    return null;
  }

  const showReview = () => {
    setSummary(getRecoverySummary());
    setStage(REVIEW_STAGE);
  };

  const closePopup = () => {
    setVisible(false);
    closeMessageBox();
  };

  const runAfterFade = (hideOverlay, action) => {
    hideOverlay(() => {
      setVisible(false);
      action();
    });
  };

  return (
    <WithOverlay className="overlay--confirm">
      {(hideOverlay) => (
        <div className={'confirm-popup confirm-popup--message confirm-popup--red fatal-recovery-popup'
          + (stage === REVIEW_STAGE ? ' fatal-recovery-popup--review' : '')}>
          <button
            className="confirm-popup__close ui-close"
            type="button"
            aria-label="Close"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              hideOverlay(closePopup);
            }}
          />
          <div className="confirm-popup-title">
            <div className="confirm-popup-title__icon">
              <ExclamationMarkIcon color={COLORS.red}/>
            </div>
            <div className="confirm-popup-title__text">
              <FormattedMessage
                id={stage === REVIEW_STAGE
                  ? 'messageBox.fatalErrorReviewTitle'
                  : 'messageBox.fatalErrorTitle'}
              />
            </div>
          </div>
          <div className="confirm-popup-body">
            {stage === PROMPT_STAGE ? (
              <FormattedMessage id="messageBox.fatalErrorRecoveryReviewText"/>
            ) : (
              <RecoveryReview summary={summary}/>
            )}
          </div>
          <div className="confirm-popup-buttons fatal-recovery-popup__buttons">
            {stage === PROMPT_STAGE ? (
                <button className="ui-button2 ui-button2--blue" type="button" onClick={showReview}>
                <FormattedMessage id="messageBox.fatalErrorReviewButton"/>
              </button>
            ) : (
              <React.Fragment>
                <button
                  className="ui-button2 ui-button2--blue"
                  type="button"
                  onClick={() => setStage(PROMPT_STAGE)}
                >
                  <FormattedMessage id="messageBox.fatalErrorReviewBack"/>
                </button>
                {quoteCanBeUpdated ? (
                  <button
                    className="ui-button2 ui-button2--red"
                    type="button"
                    onClick={() => runAfterFade(hideOverlay, saveCurrentQuote)}
                  >
                    <FormattedMessage id="messageBox.fatalErrorUpdateQuote"/>
                  </button>
                ) : null}
                <button
                  className="ui-button2 ui-button2--blue"
                  type="button"
                  onClick={() => runAfterFade(hideOverlay, openSaveAsNewQuotePopup)}
                >
                  <FormattedMessage id="messageBox.fatalErrorSaveAsNewQuote"/>
                </button>
              </React.Fragment>
            )}
          </div>
        </div>
      )}
    </WithOverlay>
  );
};

FatalErrorRecoveryPopup.propTypes = {
  initialStage: PropTypes.oneOf([PROMPT_STAGE, REVIEW_STAGE]),
};

export default FatalErrorRecoveryPopup;
